"use client";

import React, { useState, useEffect } from "react";
import { MapPin, Maximize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Project = {
  id: string;
  title: string;
  category: string;
  image: string;
  location: string;
  description: string;
};
const CATEGORIES = ["All", "Water Parks", "Amusement Parks", "Indoor Parks"];
const PROJECTS: Project[] = [{
  id: "p1",
  title: "Jurassic Water Park",
  category: "Water Parks",
  image: "https://perfect-spider.10web.cloud/wp-content/uploads/2026/03/6.jpg?v=ox1ltxn8gqp",
  location: "Shenzhen, China",
  description: "A flagship EPCO project recreating historical trading routes through immersive architecture and state-of-the-art entertainment facilities designed for global tourism appeal."
}, {
  id: "p2",
  title: "Happy Live Park",
  category: "Water Parks",
  image: "https://perfect-spider.10web.cloud/wp-content/uploads/2026/03/7.jpg?v=lyo32zwjvnl",
  location: "Zhejiang, China",
  description: "An ultra-luxury sustainable resort developed to the highest international standards, integrating smart hospitality systems with local architectural heritage."
}, {
  id: "p3",
  title: "Dinosaur Kingdom Resort",
  category: "Amusement Parks",
  image: "https://perfect-spider.10web.cloud/wp-content/uploads/2026/03/19.jpg?v=wo38j0xyz4i",
  location: "London, UK",
  description: "A comprehensive entertainment destination featuring themed lands, luxury accommodations, and advanced operations management for a seamless visitor journey."
}, {
  id: "p4",
  title: "Fantasy Park",
  category: "Amusement Parks",
  image: "https://perfect-spider.10web.cloud/wp-content/uploads/2026/03/10.1.jpg",
  location: "Guangdong, China",
  description: "A meticulously planned cultural landmark showcasing maritime history through innovative display technologies and traditional architectural motifs."
}, {
  id: "p5",
  title: "Dream Kids World",
  category: "Indoor Parks",
  image: "https://perfect-spider.10web.cloud/wp-content/uploads/2026/03/20.jpg?v=8gd57eetoy2",
  location: "Riyadh, Saudi Arabia",
  description: "A premier destination offering a blend of high-end commercial spaces and luxury hospitality, designed to drive regional tourism growth and investment."
}, {
  id: "p6",
  title: "Dinosaur Museum",
  category: "Indoor Parks",
  image: "https://perfect-spider.10web.cloud/wp-content/uploads/2026/03/14.jpg?v=0knsj4pbuqn",
  location: "Xinjiang, China",
  description: "An integrated amusement complex that leverages natural topography to provide unique thrill experiences while maintaining environmental sustainability."
}, {
  id: "p7",
  title: "Happy Valley",
  category: "Amusement Parks",
  image: "https://perfect-spider.10web.cloud/wp-content/uploads/2026/03/9-1.jpg?v=0oe61h8lauhl",
  location: "Shenzhen, China",
  description: "A meticulously planned cultural landmark showcasing maritime history through innovative display technologies and traditional architectural motifs."
}, {
  id: "p8",
  title: "Global Harbor",
  category: "Indoor Parks",
  image: "https://perfect-spider.10web.cloud/wp-content/uploads/2026/03/21.jpg?v=no3db0967wk",
  location: "Riyadh, Saudi Arabia",
  description: "A premier destination offering a blend of high-end commercial spaces and luxury hospitality, designed to drive regional tourism growth and investment."
}, {
  id: "p9",
  title: "Western Dinosaur Water Park",
  category: "Water Parks",
  image: "https://perfect-spider.10web.cloud/wp-content/uploads/2026/03/12.jpg?v=0va8475171b",
  location: "Huangshan, China",
  description: "An integrated amusement complex that leverages natural topography to provide unique thrill experiences while maintaining environmental sustainability."
}];
export default function IconicDestinations() {
  const [activeFilter, setActiveFilter] = useState<string>("All");
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const filterReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const filteredProjects = PROJECTS.filter(project => activeFilter === "All" || project.category === activeFilter);
  const handleFilterChange = (category: string) => {
    if (category === activeFilter) return;
    setIsTransitioning(true);
    setActiveFilter(category);
    setTimeout(() => {
      setIsTransitioning(false);
    }, 400);
  };
  return <section data-section-id="92475" id="global-portfolio" className="py-24 bg-background text-foreground relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-secondary/10 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {}
        <div ref={headerReveal.ref} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-1000 ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-xs font-mono tracking-[0.2em] text-secondary uppercase mb-4">
            Global Footprint
          </h2>
          <h3 className="text-4xl md:text-6xl font-serif font-bold text-primary mb-6 tracking-tight">
            Iconic Destinations
          </h3>
          <p className="text-lg text-foreground/80 leading-relaxed">
            Explore our curated portfolio of transformative cultural tourism projects. 
            From visionary planning to operational excellence, we deliver 
            world-class destinations across the Belt and Road regions.
          </p>
        </div>

        {}
        <div ref={filterReveal.ref} className={`flex flex-wrap justify-center gap-2 mb-12 transition-all duration-1000 delay-200 ${filterReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {CATEGORIES.map((category, i) => <Button key={category} data-index={i} variant={activeFilter === category ? "default" : "outline"} onClick={() => handleFilterChange(category)} className="rounded-sm px-8 py-6 font-mono text-xs uppercase tracking-widest transition-all duration-500 border-primary/20 hover:border-primary">
              {category}
            </Button>)}
        </div>

        {}
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-6">
          {isTransitioning ? Array.from({
          length: 6
        }).map((_, i) => <div key={`skeleton-${i}`} data-index={i} className="break-inside-avoid mb-6">
                  <Skeleton className="w-full rounded-sm bg-muted" style={{
            height: `${Math.floor(Math.random() * (400 - 250 + 1) + 250)}px`
          }} />
                </div>) : filteredProjects.map((project, i) => <div key={project.id} data-index={i} className="break-inside-avoid mb-6 animate-in fade-in zoom-in-95 duration-700" style={{
          animationDelay: `${i * 100}ms`
        }}>
                  <Card className="overflow-hidden group cursor-pointer border border-border/50 shadow-sm hover:shadow-2xl transition-all duration-700 rounded-sm bg-card" onClick={() => setSelectedProject(project)}>
                    <div className="relative w-full">
                      {}
                      <div className={`relative w-full grayscale-[0.3] group-hover:grayscale-0 transition-all duration-700 ${i % 3 === 0 ? "aspect-[4/5]" : i % 2 === 0 ? "aspect-square" : "aspect-[3/4]"}`}>
                        <img src={project.image} alt={project.title} className="object-cover w-full h-full transition-transform duration-1000 group-hover:scale-110" loading="lazy" />
                        
                        {}
                        <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col justify-end p-8 backdrop-blur-[2px]">
                          <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-[10px] font-mono tracking-widest px-3 py-1 bg-secondary text-secondary-foreground uppercase">
                                {project.category}
                              </span>
                              <Maximize2 className="w-5 h-5 text-secondary" />
                            </div>
                            <h4 className="text-white font-serif text-2xl font-bold mb-2">
                              {project.title}
                            </h4>
                            <div className="flex items-center text-white/70 text-sm font-sans">
                              <MapPin className="w-4 h-4 mr-1 text-secondary" />
                              {project.location}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>)}
        </div>
      </div>

      {}
      <Dialog open={!!selectedProject} onOpenChange={open => !open && setSelectedProject(null)}>
        <DialogContent className="max-w-5xl w-[95vw] p-0 overflow-hidden bg-background border-primary/10 rounded-sm shadow-2xl">
          {selectedProject && <div className="grid grid-cols-1 md:grid-cols-2 h-full max-h-[85vh]">
              <div className="relative h-[40vh] md:h-full bg-muted">
                <img src={selectedProject.image} alt={selectedProject.title} className="object-cover w-full h-full" />
              </div>
              <div className="p-8 md:p-12 flex flex-col justify-center overflow-y-auto">
                <DialogHeader className="text-left mb-6">
                  <div className="flex items-center gap-4 mb-6">
                    <span className="text-[10px] font-mono tracking-widest px-3 py-1 bg-secondary/10 text-secondary border border-secondary/20 uppercase">
                      {selectedProject.category}
                    </span>
                    <span className="flex items-center text-xs font-mono text-foreground/60 uppercase tracking-wider">
                      <MapPin className="w-4 h-4 mr-2 text-secondary" />
                      {selectedProject.location}
                    </span>
                  </div>
                  <DialogTitle className="leading-none font-serif font-bold text-primary mb-6 tracking-tight !text-3xl">Jurassic Water Park</DialogTitle>
                  <DialogDescription className="text-foreground/70 leading-relaxed font-sans !text-sm">A flagship EPCO project recreating historical trading routes through immersive architecture and state-of-the-art entertainment facilities designed for global tourism appeal.</DialogDescription>
                </DialogHeader>
                
                <div className="mt-8 pt-8 border-t border-border">
                  <h5 className="text-xs font-mono font-bold text-primary mb-6 uppercase tracking-[0.2em]">
                    Project Methodology
                  </h5>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <div className="w-1.5 h-1.5 bg-secondary mt-2 mr-4 shrink-0" />
                      <span className="text-sm text-foreground/80 leading-snug">Integrated EPCO lifecycle management for maximum ROI</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-1.5 h-1.5 bg-secondary mt-2 mr-4 shrink-0" />
                      <span className="text-sm text-foreground/80 leading-snug">Sustainable development aligned with global standards</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-1.5 h-1.5 bg-secondary mt-2 mr-4 shrink-0" />
                      <span className="text-sm text-foreground/80 leading-snug">Preservation and integration of local cultural heritage</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>}
        </DialogContent>
      </Dialog>
    </section>;
}